import {addToCartConfig} from '@udemy/cart';
import {getOSName} from '@udemy/shared-utils';
import {udLink} from '@udemy/ud-data';

export const enum VoucherProviderTypes {
    CERTIFICATION_PROVIDER_COMPTIA = 1,
    CERTIFICATION_PROVIDER_AWS = 2,
    CERTIFICATION_PROVIDER_MICROSOFT = 3,
    CERTIFICATION_PROVIDER_LINUX_FOUNDATION = 4,
}

/** Cart API `certification_product_type` values for voucher buyables. */
export const enum VoucherProductTypes {
    VOUCHER_POOL_PRODUCT_TYPE_BASE = 1,
    VOUCHER_POOL_PRODUCT_TYPE_RETAKE_INSURANCE = 2,
    VOUCHER_POOL_PRODUCT_TYPE_UDEMY_ISSUED_RETAKE_INSURANCE = 3,
}

export const getConfig = (
    gettext: (msgid: string) => string,
    interpolate: (template: string, source: unknown, isSourceObject?: boolean) => string,
    ngettext: (msgid: string, msgidPlural: string, count: number) => string,
) => {
    const defaultConfirmationPurchaseSettings = {
        leadText: gettext('Thank you for your order!'),
        moreInfo: gettext(
            'The course will be delivered to your Udemy account as soon as we receive payment confirmation ' +
                'from your bank. Once the course is delivered, you will be notified by email.',
        ),
    };

    const strings = {
        checkout: gettext('Checkout'),
        hideCodes: gettext('Hide Codes'),
        insertCode: gettext('Insert Code'),
        discountStatusText: {
            applied: gettext('<b>%(couponCode)s</b> is applied'),
            invalid: gettext('%(couponCode)s is not valid'),
            expired: gettext('%(couponCode)s has expired'),
            sold_out: gettext('%(couponCode)s has sold out'),
            unused: gettext('%(couponCode)s is not applied'),
        },
        shoppingCart: gettext('Shopping Cart'),
        emptyCartMessage: gettext(
            "<strong>Your cart is empty</strong> – let's change that. Time to learn some new skills!",
        ),
        learnersAreViewing: gettext('Learners are viewing'),
        popularTopics: gettext('Popular Topics'),
        popularTopicsItems: {
            generativeAI: gettext('Generative AI'),
            itCertifications: gettext('IT Certifications'),
            dataScience: gettext('Data Science'),
            chatGPT: gettext('ChatGPT'),
            promptEngineering: gettext('Prompt Engineering'),
            microsoftExcel: gettext('Microsoft Excel'),
            largeLanguageModels: gettext('Large Language Models'),
            machineLearning: gettext('Machine Learning'),
            aiAgents: gettext('AI Agents'),
        },
        certificationVoucher: {
            /** GQL providerDetails.name (e.g. "AWS") → localized "AWS Certification". */
            providerLabel: (issuerName: string) =>
                interpolate(gettext('%(provider)s Certification'), {provider: issuerName}, true),
            /** Fallback for cart API buyables that only expose certification_provider id. */
            providerNames: {
                [VoucherProviderTypes.CERTIFICATION_PROVIDER_COMPTIA]:
                    gettext('CompTIA Certification'),
                [VoucherProviderTypes.CERTIFICATION_PROVIDER_AWS]: gettext('AWS Certification'),
                [VoucherProviderTypes.CERTIFICATION_PROVIDER_MICROSOFT]:
                    gettext('Microsoft Certification'),
                [VoucherProviderTypes.CERTIFICATION_PROVIDER_LINUX_FOUNDATION]: gettext(
                    'Linux Foundation Certification',
                ),
            } as Record<number, string>,
            /**
             * Voucher validity bullet. Rewording any ngettext string below creates new msgids, which
             * render in English until translated in XTM and the image is rebuilt (translations are
             * baked in at build time via `pnpm build:locale`).
             */
            validityText: (expiration_date_in_months: number) => {
                return interpolate(
                    ngettext(
                        'Voucher valid for at least %s month from the purchase date',
                        'Voucher valid for at least %s months from the purchase date',
                        expiration_date_in_months,
                    ),
                    [expiration_date_in_months],
                );
            },
            /** Standard voucher exam-format bullet (AWS/CompTIA/Microsoft). */
            examFormatText: gettext('Can be used for online or in-person exams'),
            /** Country-of-purchase legal disclaimer — shown as the last bullet on every voucher card. */
            countryValidityText: gettext(
                'Exam vouchers are only valid in the country of purchase.',
            ),
            /**
             * Linux Foundation voucher bullets: 6-month redemption / 12-month scheduling
             * window, two attempts, online-only. The 6/12 durations are product constants,
             * not backend-driven, so they are intentionally hard-coded.
             */
            getLinuxBullets: () => [
                gettext(
                    'Voucher is redeemable for at least 6 months after purchase. Exam must be scheduled within 12 months after redemption',
                ),
                gettext('Two attempts included'),
                gettext('Can be used for online exam only'),
            ],
            /** Provider-issued Voucher + Retake bullets (CompTIA/Microsoft). */
            getRetakeBullets: (expiration_date_in_months: number) => [
                interpolate(
                    ngettext(
                        'Voucher and Retake (if purchased) valid for at least %s month from the purchase date',
                        'Voucher and Retake (if purchased) valid for at least %s months from the purchase date',
                        expiration_date_in_months,
                    ),
                    [expiration_date_in_months],
                ),
                gettext('Two attempts included'),
                gettext('Can be used for online or in-person exams'),
            ],
            /**
             * Udemy-issued Second Chance bullets (AWS): the base voucher and the issued
             * Second Chance voucher have separate validity windows.
             */
            getSecondChanceBullets: (expiration_date_in_months: number) => [
                interpolate(
                    ngettext(
                        'Voucher valid for at least %s month from the purchase date',
                        'Voucher valid for at least %s months from the purchase date',
                        expiration_date_in_months,
                    ),
                    [expiration_date_in_months],
                ),
                interpolate(
                    ngettext(
                        'Second Chance (if purchased and issued) valid for at least %s month from the issuance date',
                        'Second Chance (if purchased and issued) valid for at least %s months from the issuance date',
                        expiration_date_in_months,
                    ),
                    [expiration_date_in_months],
                ),
                gettext('Two attempts included'),
                gettext('Can be used for online or in-person exams'),
            ],
            voucherUpsellSectionTitle: gettext('Complete your certification prep'),
        },
    };

    const {errors, urls} = addToCartConfig;

    return {
        checkout: {
            types: ['cart', 'express'],
        },
        errors,
        shoppingList: {
            all: ['cart', 'saved_for_later', 'wishlist'],
            cartSublists: ['certification_voucher', 'course'] as const,
            sublistStrings: {
                title: {
                    certification_voucher: (items: unknown[]) =>
                        interpolate(
                            ngettext('%s Voucher in Cart', '%s Vouchers in Cart', items.length),
                            [items.length],
                        ),
                    course: (items: unknown[]) =>
                        interpolate(
                            ngettext('%s Course in Cart', '%s Courses in Cart', items.length),
                            [items.length],
                        ),
                },
            },
            listStrings: {
                title: {
                    cart: (items: unknown[]) =>
                        interpolate(
                            ngettext('%s Course in Cart', '%s Courses in Cart', items.length),
                            [items.length],
                        ),
                    saved_for_later: () => gettext('Saved for later'),
                    wishlist: () => gettext('Recently wishlisted'),
                },
                empty: {
                    cartPage: {
                        cart: gettext('Your cart is empty. Keep shopping to find a course!'),
                        saved_for_later: gettext("You haven't saved any courses for later."),
                        wishlist: gettext("You haven't added any courses to your wishlist."),
                    },
                    dropdown: {
                        cart: gettext('Your cart is empty.'),
                        wishlist: gettext('Your wishlist is empty.'),
                    },
                },
            },
        },
        strings,
        performanceMarkName: 'Cart.shopping-item-list-first-img-loaded',
        success: {
            cartSuccess: {
                leadText: gettext("You've successfully added"),
            },
            subscribe: {
                headerText: gettext('Jump right in'),
            },
            licensePurchase: {
                isConfirmation: false,
                leadText: gettext('Thanks for purchasing %(title)s!'),
                moreInfo: gettext(
                    'Invite people to take this course for free with this code! It can be used up to ' +
                        '%(remaining)s times. This redemption code is also included in the payment ' +
                        'confirmation email for your records.',
                ),
                redemptionCode: gettext('Your redemption code is:'),
            },
            multiplePurchase: {
                congratulations: gettext('Congratulations!'),
                headerText: gettext('Your New Purchases'),
                leadText: gettext("You've enrolled in %(count)s courses."),
                myCoursesUrl: '/home/my-courses',
            },
            singlePurchase: {
                headerText: gettext('Jump right in'),
            },
            licensePurchaseConfirmation: defaultConfirmationPurchaseSettings,
            singlePurchaseConfirmation: defaultConfirmationPurchaseSettings,
            mobilePurchaseConfirmation: Object.assign(
                {...defaultConfirmationPurchaseSettings},
                {
                    mobileImage: udLink.toImages(
                        `mobile/success/success_${getOSName()}_mobile.jpg`,
                    ),
                },
            ),
            multiplePurchaseConfirmation: Object.assign(
                {...defaultConfirmationPurchaseSettings},
                {
                    moreInfo: gettext(
                        'We are excited for you to start learning. The courses will be delivered ' +
                            'to your Udemy account as soon as we receive payment confirmation from ' +
                            'your bank. Once the courses are delivered, you will be notified by email ' +
                            'so you can get back to learning!',
                    ),
                },
            ),
        },
        urls,
    };
};
